import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Select,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { getAiPresets, seedAiPresets } from "../aiApi";
import { ListingEntityType, AiPromptPreset } from "./types/ai.types";
import GlobalSpinner from "../../components/CustomReusables/GlobalSpinner";

type Props = {
  entityType: ListingEntityType;
  category?: string;
  value?: string;
  onChange: (presetKey: string) => void;
};

export const AiPromptPresetPicker: React.FC<Props> = ({
  entityType,
  category,
  value,
  onChange,
}) => {
  const toast = useToast();

  const [presets, setPresets] = useState<AiPromptPreset[]>([]);
  const [loading, setLoading] = useState(false);

  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const selectBg = useColorModeValue("white", "gray.800");

  const filtered = useMemo(() => {
    return presets.filter((preset) => {
      const entityMatch =
        !preset.entityTypes?.length || preset.entityTypes.includes(entityType);
      const categoryMatch = !category || preset.category === category;

      return entityMatch && categoryMatch;
    });
  }, [presets, entityType, category]);

  const selectedPreset = filtered.find((preset) => preset.key === value);

  const loadPresets = async () => {
    setLoading(true);

    try {
      const rows = await getAiPresets();
      setPresets(rows || []);
    } catch (error: any) {
      toast({
        title: "Failed to load AI presets",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setLoading(true);

    try {
      const rows = await seedAiPresets();
      setPresets(rows || []);

      toast({
        title: "AI presets seeded",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Failed to seed presets",
        description: error?.message || "Please check backend logs.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!value && filtered[0]?.key) {
      onChange(filtered[0].key);
    }
  }, [filtered, onChange, value]);

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between">
        <Text fontSize="sm" fontWeight="semibold">
          Prompt preset
        </Text>

        {loading ? (
          <GlobalSpinner />
        ) : (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="teal"
            onClick={loadPresets}
          >
            Refresh
          </Button>
        )}
      </HStack>

      {filtered.length > 0 ? (
        <Select
          size="sm"
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          bg={selectBg}
          borderColor={borderColor}
          borderRadius="lg"
        >
          {filtered.map((preset) => (
            <option key={preset.key} value={preset.key}>
              {preset.name}
            </option>
          ))}
        </Select>
      ) : (
        <Box
          p={4}
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
        >
          <Text fontSize="sm" color={mutedText}>
            No AI presets found yet.
          </Text>

          <Button
            mt={3}
            size="sm"
            colorScheme="teal"
            onClick={handleSeed}
            isLoading={loading}
          >
            Seed default presets
          </Button>
        </Box>
      )}

      {selectedPreset && (
        <Box
          p={3}
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
        >
          <HStack spacing={2} mb={1}>
            <Badge colorScheme="teal" borderRadius="full">
              {selectedPreset.category}
            </Badge>
            {selectedPreset.isSystem && (
              <Badge colorScheme="purple" borderRadius="full">
                System
              </Badge>
            )}
          </HStack>

          <Text fontSize="xs" color={mutedText}>
            {selectedPreset.description || "Reusable PickDengu AI prompt."}
          </Text>
        </Box>
      )}
    </VStack>
  );
};
